/**
 * Emits the admin tokens as CSS custom properties so plain CSS (and Tailwind
 * utilities) can reach the same values `sx` reads off the MUI theme.
 *
 * Injected once at the app root via `<GlobalStyles>` rather than kept as a
 * hand-written stylesheet — generating it from `adminTokens` is what makes the
 * "CSS var and MUI theme always match" acceptance criterion structural.
 */
import type { AdminTokens, TypeToken } from './tokens';
import { adminTokens } from './tokens';

/**
 * Namespaced deliberately. `@keyframes` names are global to the document, and
 * the admin loads Tailwind (via `@s2w/ui`'s stylesheet), which ships its own
 * `pulse` keyframe behind `animate-pulse`. A bare `pulse` here would quietly
 * replace it — a screen reaching for `animate-pulse` would get this box-shadow
 * ripple instead of Tailwind's opacity fade, with nothing logged either way.
 */
const PULSE_KEYFRAME = 'admin-pulse';

/**
 * Namespaced for the same reason as the pulse: `spin` is a common keyframe name
 * and Tailwind ships `animate-spin`. Drives the market badge's queued-to-sync
 * refresh icon.
 */
const SPIN_KEYFRAME = 'admin-spin';

/**
 * Namespaced for the same reason again, and separate from the live dot's pulse
 * because it is a different ring: `admin-pulse` ripples the status pill's green
 * at 6px, where this breathes the accent blue behind the Schedule rail's
 * current milestone. Same shape, different colour and reach, so they cannot
 * share one keyframe.
 */
const STEP_PULSE_KEYFRAME = 'admin-step-pulse';

/**
 * `surfaceMuted` -> `surface-muted`, `ink2` -> `ink-2`, but `h1` stays `h1`.
 *
 * Exported because the gallery labels swatches with these names: a second copy
 * would let the labels drift from the properties actually on `:root`.
 */
export const toCssName = (key: string): string =>
  key
    .replace(/([a-z])([A-Z0-9])/g, '$1-$2')
    .toLowerCase()
    .replace(/^h-(\d)$/, 'h$1');

const typeVariables = (name: string, token: TypeToken): Record<string, string> => {
  const prefix = `--text-${toCssName(name)}`;
  return {
    [`${prefix}-family`]: token.fontFamily,
    [`${prefix}-size`]: token.fontSize,
    [`${prefix}-weight`]: String(token.fontWeight),
    ...(token.letterSpacing ? { [`${prefix}-tracking`]: token.letterSpacing } : {}),
    ...(token.textTransform ? { [`${prefix}-transform`]: token.textTransform } : {}),
    ...(token.textWrap ? { [`${prefix}-wrap`]: token.textWrap } : {}),
    ...(token.lineHeight ? { [`${prefix}-line-height`]: String(token.lineHeight) } : {}),
  };
};

/**
 * The full `:root` custom-property set, as a flat `--name` -> value map.
 * Exported separately from the global styles so tests can assert parity.
 */
export const buildCssVariables = (tokens: AdminTokens = adminTokens): Record<string, string> => ({
  ...Object.fromEntries(
    Object.entries(tokens.color).map(([key, value]) => [`--${toCssName(key)}`, value]),
  ),
  ...Object.fromEntries(
    Object.entries(tokens.status).flatMap(([status, palette]) =>
      Object.entries(palette).map(([slot, value]) => [`--status-${status}-${slot}`, value]),
    ),
  ),
  '--status-pill-padding': tokens.statusPill.padding,
  '--status-pill-gap': `${tokens.statusPill.gap}px`,
  '--status-pill-dot': `${tokens.statusPill.dotSize}px`,
  '--status-pill-label-size': tokens.statusPill.labelSize,
  '--status-pill-label-weight': String(tokens.statusPill.labelWeight),
  '--status-pill-label-tracking': tokens.statusPill.labelTracking,
  ...Object.fromEntries(
    Object.entries(tokens.radius).map(([key, value]) => [
      `--radius-${toCssName(key)}`,
      `${value}px`,
    ]),
  ),
  ...Object.fromEntries(
    Object.entries(tokens.space).map(([key, value]) => [`--space-${key}`, `${value}px`]),
  ),
  ...Object.fromEntries(
    Object.entries(tokens.layout).map(([key, value]) => [
      `--layout-${toCssName(key)}`,
      `${value}px`,
    ]),
  ),
  '--nav-rail-width-expanded': `${tokens.navRail.widthExpanded}px`,
  '--nav-rail-width-collapsed': `${tokens.navRail.widthCollapsed}px`,
  '--nav-rail-collapse-below': `${tokens.navRail.collapseBelow}px`,
  '--nav-rail-item-padding': tokens.navRail.itemPadding,
  '--nav-rail-label-size': tokens.navRail.labelSize,
  '--nav-rail-avatar-size': `${tokens.navRail.avatarSize}px`,
  '--button-padding': tokens.button.padding,
  '--button-outline-border': tokens.button.outlineBorder,
  '--button-outline-border-hover': tokens.button.outlineBorderHover,
  '--button-primary-hover': tokens.button.primaryHover,
  '--border-width': tokens.border.width,
  '--border-default': tokens.border.default,
  '--shadow-card': tokens.shadow.card,
  '--shadow-tab-selected': tokens.shadow.tabSelected,
  '--shadow-current-step-ring': tokens.shadow.currentStepRing,
  '--shadow-toggle-thumb': tokens.shadow.toggleThumb,
  '--shadow-popover': tokens.shadow.popover,
  '--shadow-modal': tokens.shadow.modal,
  // Prefixed deliberately. `--font-sans` / `--font-mono` are Tailwind v4 theme
  // keys, and `@s2w/ui` publishes `--font-sans: 'Roboto', …` (plus
  // `--font-heading: var(--font-sans)`) from its own `@theme` block. Emitting
  // bare names here reaches across that package boundary: this `:root`, injected
  // when React mounts, would silently retarget Tailwind's `--default-font-family`
  // and every `font-sans` / `font-heading` utility inside `@s2w/ui` components
  // rendered in admin — and only after mount, so the first paint would use
  // Roboto and then reflow. Admin's own surfaces read the `--admin-` names.
  '--admin-font-sans': tokens.font.sans,
  '--admin-font-mono': tokens.font.mono,
  '--admin-font-feature-settings': tokens.font.featureSettings,
  ...Object.fromEntries(
    Object.entries(tokens.text).flatMap(([name, token]) =>
      Object.entries(typeVariables(name, token)),
    ),
  ),
  '--tab-selected-weight': String(tokens.tabSelectedWeight),
  // Shorthand for the live status dot so consumers don't re-state the timing.
  // Consumers read this variable, never the keyframe name — which is why
  // PULSE_KEYFRAME can stay namespaced without any caller caring.
  '--animation-pulse': `${PULSE_KEYFRAME} 1.6s infinite`,
  '--animation-spin': `${SPIN_KEYFRAME} 2s linear infinite`,
  '--animation-step-pulse': `${STEP_PULSE_KEYFRAME} 1.8s infinite`,
});

/**
 * Root-level styles: the token variables, the body type defaults, and the
 * live-status-dot keyframe.
 */
export const adminGlobalStyles = {
  ':root': buildCssVariables(),
  body: {
    fontFamily: 'var(--admin-font-sans)',
    fontFeatureSettings: 'var(--admin-font-feature-settings)',
    WebkitFontSmoothing: 'antialiased',
    backgroundColor: 'var(--bg)',
    color: 'var(--ink)',
  },
  [`@keyframes ${PULSE_KEYFRAME}`]: {
    '0%': { boxShadow: '0 0 0 0 rgba(31,157,85,.6)' },
    '100%': { boxShadow: '0 0 0 6px rgba(31,157,85,0)' },
  },
  [`@keyframes ${SPIN_KEYFRAME}`]: {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
  },
  [`@keyframes ${STEP_PULSE_KEYFRAME}`]: {
    '0%': { boxShadow: '0 0 0 0 rgba(37,99,235,.45)' },
    '70%': { boxShadow: '0 0 0 6px rgba(37,99,235,0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(37,99,235,0)' },
  },
  /**
   * All three of the admin's perpetual animations — the live status dot's
   * pulse, the queued-to-sync spinner and the Schedule rail's current
   * milestone — stop for a reader who has asked the OS for reduced motion. They are decorative: the pill still says "Live" and the
   * badge still says "Queued to sync" and the rail's current step keeps its ring,
   * so nothing is lost by holding them still.
   * Set on the variables rather than the keyframes so every consumer is covered
   * by the same rule that publishes them.
   */
  '@media (prefers-reduced-motion: reduce)': {
    ':root': {
      '--animation-pulse': 'none',
      '--animation-spin': 'none',
      '--animation-step-pulse': 'none',
    },
  },
} as const;
